import type { AxiosError } from "axios";
import axios from "axios";

const getServerErrorMessage = (error: AxiosError, genericMessage: string) => {
  const data = error.response?.data;
  if (typeof data === "string") return data;
  if (
    data &&
    typeof data === "object" &&
    typeof (data as { message?: unknown }).message === "string"
  ) {
    return (data as { message: string }).message;
  }
  return genericMessage;
};

export const handleAPIError = (
  error: unknown,
): { code?: number; message: string; suppress?: boolean } => {
  // Must come first — a 401 in mid-redirect carries a Fastify body
  // ("jwt expired" etc.) we don't want surfacing in a snackbar after
  // the response interceptor has already started the logout flow.
  if ((error as { silentAuth?: boolean })?.silentAuth) {
    return { code: 401, message: "", suppress: true };
  }

  let code: number | undefined;
  let message = "Unknown Error";

  if (axios.isAxiosError(error)) {
    code = error.response?.status;
    switch (code) {
      case 400:
        message = getServerErrorMessage(error, "Bad Request");
        break;
      case 401:
        message = getServerErrorMessage(error, "Unauthorized");
        break;
      case 403:
        message = getServerErrorMessage(error, "Forbidden");
        break;
      case 404:
        message = getServerErrorMessage(error, "Not Found");
        break;
      case 409:
        message = getServerErrorMessage(error, "Conflict");
        break;
      default:
        message = getServerErrorMessage(error, "Unknown Error");
    }
  } else if (error instanceof Error) {
    message = error.message;
  }

  return { code, message };
};

export type APIError = Error & { code?: number; suppress?: boolean };

/**
 * Convert a caught Axios/Error into an Error suitable for rethrow,
 * preserving the HTTP status code so callers can branch on it
 * (e.g. localise 409 differently from 500).
 */
export const toAPIError = (error: unknown): APIError => {
  const parsed = handleAPIError(error);
  const wrapped = new Error(parsed.message) as APIError;
  wrapped.code = parsed.code;
  if (parsed.suppress) wrapped.suppress = true;
  return wrapped;
};
